import * as XLSX from "xlsx";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { singleProp } from "@/interface/dataInterface";
import { useRefresh } from "react-admin";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { LoaderCircle} from "lucide-react";
import { z } from "zod";
import axiosInstance from "@/utils/axiosInstance";

const ExcelUploadPage: FC<singleProp> = ({ prop }) => {
  const refresh = useRefresh();
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dataArray, setDataArray] = useState([]);
  const [failedRows, setFailedRows] = useState<any[]>([]);
  const [validRows, setValidRows] = useState<any[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setLoading(true);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target?.result;
      if (!data) return;

      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheet];
      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        defval: null,
      });
      setDataArray([...jsonData] as any);
    };
    reader.readAsArrayBuffer(file);
    setSelectedFile(file);
    setLoading(false);
  }, []);

  const submitData = async () => {
    if (dataArray.length == 0) {
      toast("Data is missing");
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post(
        `/${prop}/bulk`,
        {items: validRows},
      );
      if (response.data.failedRows) {
        setFailedRows(response.data.failedRows);
        setValidRows(response.data.successRows);
        return;
      }

      toast("All records added");
      refresh();
    } catch (err: any) {
      toast(err.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dataArray.length == 0) return;
    const seenSKU: string[] = [];
    const seenEmails: string[] = [];
    const seenVINS: string[] = [];
    const rows: any[] = [];
    const correctRows: any[] = [];
    dataArray.map((item: any) => {
      if (item?.SKU) {
        if (seenSKU.includes(item?.SKU)) {
          rows.push({ data: item, reason: "Duplicate SKU value" });
          return;
        }

        seenSKU.push(item.SKU);
      }

      if (item?.email) {
        if (seenEmails.includes(item?.email)) {
          rows.push({ data: item, reason: "Duplicate email value" });
          return;
        }

        seenEmails.push(item?.email);
      }

      if (item?.vin) {
        if (seenVINS.includes(item?.vin)) {
          rows.push({ data: item, reason: "Duplicate vin value" });
          return;
        }

        seenVINS.push(item?.vin);
      }

      if (item.email) {
        const emailSchema = z.string().email();
        const emailValidation = emailSchema.safeParse(item.email);
        if (!emailValidation.success) {
          rows.push({
            data: item,
            reason: "Entered a invalid email address format",
          });
          return;
        }
      }

      const keys = Object.keys(item);
      let hasEmpty = false;

      for (let i = 0; i < keys.length; i++) {
        if (!item[keys[i]]) {
          rows.push({ data: item, reason: `${keys[i]} value is empty` });
          hasEmpty = true;
          break;
        }
      }

      if (!hasEmpty) correctRows.push(item);
    });

    setFailedRows([...rows]);
    setValidRows([...correctRows]);
  }, [dataArray]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
        ".csv",
        ".xls",
      ],
    },
    multiple: false,
  });

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">
        {prop[0].toUpperCase() + prop.slice(1)} Upload
      </h1>
      <Card className="p-4 mb-4">
        <CardContent>
          <CardHeader className="!ps-0">
            <CardTitle>Import bulk data</CardTitle>
            <CardDescription>Expected file: .csv, .xlsx, .xls</CardDescription>
            <a
              ref={linkRef}
              href={`/templates/${prop}-template.csv`}
              className="hidden"
              download
            >
              Download starting template
            </a>
          </CardHeader>

          <Button className="my-5" onClick={() => linkRef.current?.click()}>
            Download starting template
          </Button>

          <div
            {...getRootProps()}
            className={`my-5 border-2 border-dashed p-6 rounded cursor-pointer text-center ${
              isDragActive ? "border-blue-600 bg-blue-50" : "border-gray-300"
            }`}
          >
            <input {...getInputProps()} />
            {selectedFile ? (
              <p className="leading-7">Selected file: {selectedFile.name}</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {isDragActive
                  ? "Drop the Excel file here..."
                  : "Drag & drop or click to upload an Excel file"}
              </p>
            )}
          </div>

          {dataArray.length > 0 && failedRows.length == 0 && (
            <div className="text-2xl text-center font-bold">
              All records valid and ready to go
            </div>
          )}
          {failedRows.length > 0 && (
            <div className="p-4">
              <CardHeader className="!ps-0 mb-3">
                <CardTitle>
                  Found {failedRows.length} invalid records. Only{" "}
                  {validRows.length} records will be inserted
                </CardTitle>
                <CardDescription>
                  Fix the records, select another file or continue with the
                  valid ones only (The records displayed in the table below wont
                  be added)
                </CardDescription>
              </CardHeader>
              <Table className="!p-4">
                <TableHeader>
                  <TableRow>
                    {Object.keys(dataArray[0]).map((key) => (
                      <TableHead key={key}>{key}</TableHead>
                    ))}
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {failedRows.map((row, idx) => (
                    <TableRow key={idx}>
                      {Object.keys(row.data).map((key) => (
                        <TableCell key={key}>{row.data[key]}</TableCell>
                      ))}
                      <TableCell className="text-red-600">
                        {row.reason}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="flex justify-end">
            {/* {failedRows.length > 0 && <Button onClick={submitData} disabled={loading}>
                Go back
              </Button>} */}
            <Button onClick={submitData} disabled={loading}>
              {loading ? (
                <>
                  <LoaderCircle className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExcelUploadPage;
